import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  ArrowRight,
  Calendar,
  Mail,
  Phone,
  Users,
  CheckCircle2,
  Loader2,
  Send,
  ShieldCheck,
  ChevronRight,
  Check,
  Globe,
  Clock
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useLifely } from '../context/LifelyContext';
import { MAP_TYPES } from '../store/mockData';
import { toast } from 'sonner';
import api, { continuityAPI } from '../services/api';

// Common timezones grouped by region
const TIMEZONE_OPTIONS = [
  { value: 'Pacific/Auckland', label: 'Auckland (GMT+12/+13)', region: 'Pacific' },
  { value: 'Australia/Sydney', label: 'Sydney (GMT+10/+11)', region: 'Australia' },
  { value: 'Australia/Perth', label: 'Perth (GMT+8)', region: 'Australia' },
  { value: 'Asia/Tokyo', label: 'Tokyo (GMT+9)', region: 'Asia' },
  { value: 'Asia/Singapore', label: 'Singapore (GMT+8)', region: 'Asia' },
  { value: 'Asia/Dubai', label: 'Dubai (GMT+4)', region: 'Asia' },
  { value: 'Europe/London', label: 'London (GMT/+1)', region: 'Europe' },
  { value: 'Europe/Paris', label: 'Paris (GMT+1/+2)', region: 'Europe' },
  { value: 'Europe/Berlin', label: 'Berlin (GMT+1/+2)', region: 'Europe' },
  { value: 'America/New_York', label: 'New York (GMT-5/-4)', region: 'Americas' },
  { value: 'America/Chicago', label: 'Chicago (GMT-6/-5)', region: 'Americas' },
  { value: 'America/Denver', label: 'Denver (GMT-7/-6)', region: 'Americas' },
  { value: 'America/Los_Angeles', label: 'Los Angeles (GMT-8/-7)', region: 'Americas' },
  { value: 'UTC', label: 'UTC (GMT+0)', region: 'Other' },
];

// Notification time presets
const NOTIFICATION_TIMES = [
  { value: 'morning', label: 'Morning', time: '9:00 AM', description: 'Start your day with a check-in' },
  { value: 'afternoon', label: 'Afternoon', time: '2:00 PM', description: 'Mid-day reminder' },
  { value: 'evening', label: 'Evening', time: '7:00 PM', description: 'End of day check-in' },
];

// Step indicator component
const StepIndicator = ({ currentStep, totalSteps }) => {
  const steps = [
    { num: 1, label: 'Frequency' },
    { num: 2, label: 'Contact' },
    { num: 3, label: 'Nominees' },
  ];

  return (
    <div className="flex items-center justify-center gap-2 mb-8">
      {steps.map((step, idx) => (
        <React.Fragment key={step.num}>
          <div className="flex items-center gap-2">
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-all
              ${currentStep > step.num 
                ? 'bg-primary text-primary-foreground' 
                : currentStep === step.num 
                  ? 'bg-primary text-primary-foreground ring-4 ring-primary/20' 
                  : 'bg-muted text-muted-foreground'
              }
            `}>
              {currentStep > step.num ? <Check className="w-4 h-4" /> : step.num}
            </div>
            <span className={`text-sm hidden sm:block ${currentStep >= step.num ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
              {step.label}
            </span>
          </div>
          {idx < steps.length - 1 && (
            <ChevronRight className="w-4 h-4 text-muted-foreground mx-2" />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// Frequency presets
const FREQUENCY_PRESETS = [
  { value: '7-days', interval: 7, unit: 'days', label: 'Every week', description: 'For high-frequency check-ins' },
  { value: '14-days', interval: 14, unit: 'days', label: 'Every 2 weeks', description: 'For active users' },
  { value: '30-days', interval: 30, unit: 'days', label: 'Every month', description: 'Recommended' },
  { value: '90-days', interval: 90, unit: 'days', label: 'Every 3 months', description: 'For occasional check-ins' },
];

const ContinuityWizard = () => {
  const navigate = useNavigate();
  const { 
    user, 
    contacts,
    maps,
    userContactPrefs,
    continuityConfig,
    updateContinuityConfig,
    saveContinuitySettings,
    addShareSetting,
    fetchContacts,
    fetchSharing,
    shareSettings,
    isAuthenticated,
    loading,
  } = useLifely();
  
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Step 1: Frequency & Timezone
  const [selectedFrequency, setSelectedFrequency] = useState('30-days');
  const [timezone, setTimezone] = useState('UTC');
  const [notificationTime, setNotificationTime] = useState('morning');
  
  // Step 2: Contact method
  const [useEmail, setUseEmail] = useState(true);
  const [useSms, setUseSms] = useState(false);
  const [emailVerified, setEmailVerified] = useState(false);
  const [verificationSending, setVerificationSending] = useState(false);
  const [verificationPending, setVerificationPending] = useState(false);
  // Refs so the polling loop sees the latest verified state (avoids stale closure)
  // and so we can clear the interval on unmount.
  const emailVerifiedRef = useRef(false);
  const pollIntervalRef = useRef(null);
  
  // Step 3: Nominees
  const [selectedNominees, setSelectedNominees] = useState([]);
  
  // Auto-detect timezone on mount
  useEffect(() => {
    try {
      const detectedTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      // Check if detected timezone is in our list
      const found = TIMEZONE_OPTIONS.find(tz => tz.value === detectedTz);
      if (found) {
        setTimezone(detectedTz);
      } else {
        // Try to find a matching region timezone
        console.log('Detected timezone not in list:', detectedTz);
      }
    } catch (e) {
      console.log('Could not detect timezone');
    }
  }, []);
  
  // Load data
  useEffect(() => {
    const loadData = async () => {
      if (isAuthenticated && !dataLoaded) {
        try {
          await Promise.all([fetchContacts(), fetchSharing()]);
          setDataLoaded(true);
        } catch (err) {
          console.error('Failed to load data:', err);
        }
      }
    };
    loadData();
  }, [isAuthenticated, dataLoaded, fetchContacts, fetchSharing]);

  // Check if email is verified from config
  useEffect(() => {
    if (continuityConfig?.email_verified === 1) {
      setEmailVerified(true);
      emailVerifiedRef.current = true;
    }
  }, [continuityConfig]);

  // Clear any active verification poll on unmount
  useEffect(() => () => {
    if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
  }, []);

  // Get email to use - respect the useAlternativeEmail preference
  const targetEmail = (userContactPrefs?.useAlternativeEmail && userContactPrefs?.alternativeEmail) 
    ? userContactPrefs.alternativeEmail 
    : user?.email;
  const hasPhone = !!userContactPrefs?.phone;

  // Handle email verification
  const handleSendVerification = async () => {
    try {
      setVerificationSending(true);
      const response = await continuityAPI.sendVerification({ source: 'wizard' });

      if (response.success) {
        setVerificationPending(true);
        toast.success('Verification email sent! Check your inbox.');
        // Start polling for verification status
        startVerificationPolling();
      }
    } catch (error) {
      toast.error('Failed to send verification email');
    } finally {
      setVerificationSending(false);
    }
  };

  // Poll for email verification status after sending test email
  const startVerificationPolling = () => {
    let attempts = 0;
    const maxAttempts = 60; // Poll for up to 5 minutes (every 5 seconds)
    const interval = setInterval(async () => {
      attempts++;
      if (attempts >= maxAttempts || emailVerifiedRef.current) {
        clearInterval(interval);
        return;
      }
      try {
        const res = await api.get('/api/continuity');
        if (res.data.continuity?.email_verified === 1) {
          setEmailVerified(true);
          emailVerifiedRef.current = true;
          setVerificationPending(false);
          clearInterval(interval);
          toast.success('Email verified successfully!');
        }
      } catch {
        // Silently continue polling
      }
    }, 5000);
    pollIntervalRef.current = interval;
  };

  // Toggle nominee selection
  const toggleNominee = (contactId) => {
    setSelectedNominees(prev => {
      const existing = prev.find(n => n.contactId === contactId);
      if (existing) {
        return prev.filter(n => n.contactId !== contactId);
      }
      return [...prev, { contactId, shareAll: true, selectedMaps: [] }];
    });
  };

  // Update nominee share settings
  const updateNomineeShare = (contactId, shareAll, selectedMaps = []) => {
    setSelectedNominees(prev => 
      prev.map(n => n.contactId === contactId ? { ...n, shareAll, selectedMaps } : n)
    );
  };

  // Get frequency values from preset
  const getFrequencyValues = () => {
    const preset = FREQUENCY_PRESETS.find(p => p.value === selectedFrequency);
    return preset || { interval: 30, unit: 'days' };
  };

  // Can proceed to next step?
  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return !!selectedFrequency;
      case 2:
        return useEmail; // SMS disabled for now (Plus plan only)
      case 3:
        return selectedNominees.length > 0;
      default:
        return false;
    }
  };

  // Handle final activation
  const handleActivate = async () => {
    if (selectedNominees.length === 0) {
      toast.error('Please select at least one nominee');
      return;
    }

    try {
      setSaving(true);
      
      const freq = getFrequencyValues();
      
      // 1. Update continuity config with timezone and notification time
      await updateContinuityConfig({
        checkInFrequency: freq.interval,
        checkInInterval: freq.interval,
        checkInUnit: freq.unit,
        useEmail,
        useSms: false, // SMS disabled for now
        timezone,
        notificationTime,
        isConfigured: true,
        isActive: true,
      });
      
      // 2. Create sharing rules for each nominee
      for (const nominee of selectedNominees) {
        const contact = contacts.find(c => c.id === nominee.contactId);
        
        // Check if sharing rule already exists
        const existingShare = shareSettings.find(
          s => s.contactId === nominee.contactId && (s.shareTrigger === 'continuity' || s.shareTrigger === 'both')
        );
        
        if (!existingShare) {
          await addShareSetting({
            contactId: nominee.contactId,
            contactName: contact?.name || 'Unknown',
            shareType: nominee.shareAll ? 'all_maps' : 'selected_maps',
            selectedMaps: nominee.shareAll ? [] : nominee.selectedMaps,
            includeWillStatus: true,
            includeContacts: false,
            shareTrigger: 'continuity',
            notes: '',
          });
        }
      }
      
      // 3. Activate continuity
      await saveContinuitySettings();
      
      toast.success('Continuity activated! You\'re all set.');
      navigate('/continuity');
    } catch (err) {
      toast.error('Failed to activate continuity');
      console.error('Activation error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Show loading
  if (loading.contacts && !dataLoaded) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="continuity-wizard">
      {/* Back link */}
      <Link 
        to="/continuity" 
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Continuity
      </Link>

      {/* Header */}
      <div className="text-center">
        <h1 className="text-2xl font-heading font-semibold">Set Up Continuity</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Choose how often we check in, and who to notify if you stop responding.
        </p>
      </div>

      {/* Combined info */}
      <Card className="bg-muted/50 border border-border" data-testid="wizard-info">
        <CardContent className="p-4 space-y-2">
          <div className="flex items-start gap-2.5">
            <ShieldCheck className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              Your data stays private - nominees only receive what you configure in Sharing Settings.
            </p>
          </div>
          <div className="flex items-start gap-2.5">
            <Mail className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
            <p className="text-sm text-muted-foreground">
              Check-in emails come from <strong className="font-medium text-foreground">noreply@mail.lifely.guide</strong> - add to contacts to avoid spam.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Step indicator */}
      <StepIndicator currentStep={currentStep} totalSteps={3} />

      {/* Step 1: Frequency */}
      {currentStep === 1 && (
        <Card className="border border-border">
          <CardContent className="p-6 space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Calendar className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-medium">How often should we check in?</h2>
                <p className="text-sm text-muted-foreground">Choose how frequently you want to receive check-in reminders</p>
              </div>
            </div>

            <RadioGroup value={selectedFrequency} onValueChange={setSelectedFrequency} className="space-y-3">
              {FREQUENCY_PRESETS.map((preset) => (
                <div key={preset.value} className="relative">
                  <RadioGroupItem
                    value={preset.value}
                    id={preset.value}
                    className="peer sr-only"
                  />
                  <Label
                    htmlFor={preset.value}
                    className={`
                      flex items-center justify-between p-4 rounded-lg border cursor-pointer transition-all
                      ${selectedFrequency === preset.value 
                        ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                        : 'border-border hover:border-primary/50 hover:bg-muted/50'
                      }
                    `}
                  >
                    <div>
                      <p className="font-medium">{preset.label}</p>
                      <p className="text-sm text-muted-foreground">{preset.description}</p>
                    </div>
                    {selectedFrequency === preset.value && (
                      <CheckCircle2 className="w-5 h-5 text-primary" />
                    )}
                  </Label>
                </div>
              ))}
            </RadioGroup>

            {/* Timezone & Notification Time */}
            <div className="pt-4 border-t border-border space-y-4">
              <div className="flex items-center gap-3">
                <Globe className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1">
                  <Label className="text-sm font-medium">Your timezone</Label>
                  <Select value={timezone} onValueChange={setTimezone}>
                    <SelectTrigger className="mt-1.5">
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONE_OPTIONS.map((tz) => (
                        <SelectItem key={tz.value} value={tz.value}>
                          {tz.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-muted-foreground" />
                <div className="flex-1">
                  <Label className="text-sm font-medium">Preferred reminder time</Label>
                  <RadioGroup value={notificationTime} onValueChange={setNotificationTime} className="mt-2 flex gap-3">
                    {NOTIFICATION_TIMES.map((time) => (
                      <div key={time.value} className="flex-1">
                        <RadioGroupItem
                          value={time.value}
                          id={`time-${time.value}`}
                          className="peer sr-only"
                        />
                        <Label
                          htmlFor={`time-${time.value}`}
                          className={`
                            flex flex-col items-center p-3 rounded-lg border cursor-pointer transition-all text-center
                            ${notificationTime === time.value 
                              ? 'border-primary bg-primary/5' 
                              : 'border-border hover:border-primary/50'
                            }
                          `}
                        >
                          <p className="font-medium text-sm">{time.label}</p>
                          <p className="text-xs text-muted-foreground">{time.time}</p>
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              You can change these settings later.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Contact Method */}
      {currentStep === 2 && (
        <Card className="border border-border">
          <CardContent className="p-6 space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-medium">How should we reach you?</h2>
                <p className="text-sm text-muted-foreground">Select how you'd like to receive check-in reminders</p>
              </div>
            </div>

            {/* Email option */}
            <div 
              className={`p-4 rounded-lg border cursor-pointer transition-all ${
                useEmail ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
              }`}
              onClick={() => setUseEmail(!useEmail)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Checkbox checked={useEmail} onCheckedChange={setUseEmail} />
                  <div>
                    <p className="font-medium">Email</p>
                    <p className="text-sm text-muted-foreground">{targetEmail}</p>
                  </div>
                </div>
                {useEmail && (
                  <Badge 
                    variant="outline" 
                    className={emailVerified 
                      ? "bg-primary/10 text-primary border-primary/30" 
                      : "bg-amber-50 text-amber-700 border-amber-300"
                    }
                  >
                    {emailVerified ? (
                      <><ShieldCheck className="w-3 h-3 mr-1" /> Verified</>
                    ) : (
                      <>Not Verified</>
                    )}
                  </Badge>
                )}
              </div>
              
              {/* Email verification test */}
              {useEmail && (
                <div className="mt-4 pt-4 border-t border-border">
                  {emailVerified ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className="w-4 h-4 text-primary" />
                      <span>Email delivery confirmed. You're all set to receive check-ins.</span>
                    </div>
                  ) : verificationPending ? (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-amber-700">
                        <Mail className="w-4 h-4" />
                        <span>Test email sent! Check your inbox and click the link to verify.</span>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleSendVerification(); }}
                        disabled={verificationSending}
                        className="text-xs"
                      >
                        Resend test email
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="text-xs text-muted-foreground">
                        We recommend testing email delivery before activating continuity.
                      </p>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={(e) => { e.stopPropagation(); handleSendVerification(); }}
                        disabled={verificationSending}
                      >
                        {verificationSending ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4 mr-2" />
                        )}
                        Send Test Email
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* SMS option - Plus plan only */}
            <div 
              className="p-4 rounded-lg border border-border bg-muted/30 opacity-70 cursor-not-allowed"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Checkbox checked={false} disabled />
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      SMS
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Receive check-in reminders via text message
                    </p>
                  </div>
                </div>
                <Badge variant="outline" className="bg-amber-100 text-amber-700 border-amber-300 text-xs">
                  Plus Plan
                </Badge>
              </div>
            </div>

            {!useEmail && (
              <p className="text-sm text-destructive">Please select at least one contact method</p>
            )}

            <div className="text-xs text-muted-foreground space-y-1">
              <p>
                <strong>Tip:</strong> You can set an alternative email for check-ins in your{' '}
                <Link to="/contacts" className="text-primary hover:underline">Contact details</Link>.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Nominees */}
      {currentStep === 3 && (
        <Card className="border border-border">
          <CardContent className="p-6 space-y-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h2 className="font-medium">Who should receive your information?</h2>
                <p className="text-sm text-muted-foreground">Select contacts to notify if you stop responding</p>
              </div>
            </div>

            {/* Info box explaining the flow */}
            <div className="p-4 rounded-lg bg-primary/5 border border-primary/20 space-y-3">
              <p className="text-sm text-foreground">
                <strong>How it works:</strong> If you don't respond to check-in reminders, 
                the contacts you select below will be notified automatically.
              </p>
              {/* Recheck nudge */}
              <div className="flex items-center justify-between gap-3 pt-2 border-t border-primary/20">
                <div className="flex items-start gap-2">
                  <div className="w-7 h-7 rounded-md bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-primary">3×</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">Default: 3 check-in attempts</p>
                    <p className="text-xs text-muted-foreground">
                      We'll try 3 times before notifying your nominees. You can change this to 1-5.
                    </p>
                  </div>
                </div>
                <Link
                  to="/continuity/settings"
                  className="text-xs text-primary font-medium hover:underline shrink-0 flex items-center gap-1"
                >
                  Customise
                  <ChevronRight className="w-3 h-3" />
                </Link>
              </div>
            </div>

            {contacts.length === 0 ? (
              <div className="text-center py-8">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                <p className="text-muted-foreground mb-4">You haven't added any contacts yet.</p>
                <Button asChild>
                  <Link to="/contacts">Add Contacts First</Link>
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {contacts.map((contact) => {
                  const isSelected = selectedNominees.some(n => n.contactId === contact.id);
                  const nominee = selectedNominees.find(n => n.contactId === contact.id);
                  
                  return (
                    <div 
                      key={contact.id}
                      className={`p-4 rounded-lg border transition-all ${
                        isSelected ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <div 
                        className="flex items-center justify-between cursor-pointer"
                        onClick={() => toggleNominee(contact.id)}
                      >
                        <div className="flex items-center gap-3">
                          <Checkbox checked={isSelected} />
                          <div>
                            <p className="font-medium">{contact.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {contact.relationship && `${contact.relationship} · `}
                              {contact.email || 'No email'}
                            </p>
                          </div>
                        </div>
                        {isSelected && (
                          <CheckCircle2 className="w-5 h-5 text-primary" />
                        )}
                      </div>
                      
                      {/* Share options when selected */}
                      {isSelected && (
                        <div className="mt-4 pt-4 border-t border-border space-y-3">
                          <Label className="text-sm text-muted-foreground">What should they receive?</Label>
                          <RadioGroup 
                            value={nominee?.shareAll ? 'all' : 'selected'} 
                            onValueChange={(v) => updateNomineeShare(contact.id, v === 'all')}
                            className="space-y-2"
                          >
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="all" id={`${contact.id}-all`} />
                              <Label htmlFor={`${contact.id}-all`} className="text-sm font-normal cursor-pointer">
                                All maps & formal arrangements
                              </Label>
                            </div>
                            <div className="flex items-center gap-2">
                              <RadioGroupItem value="selected" id={`${contact.id}-selected`} />
                              <Label htmlFor={`${contact.id}-selected`} className="text-sm font-normal cursor-pointer">
                                Selected maps only
                              </Label>
                            </div>
                          </RadioGroup>
                          
                          {!nominee?.shareAll && (
                            <div className="pl-6 space-y-2">
                              {maps.filter(m => m.id !== 'formal-arrangements').map((map) => (
                                <div key={map.id} className="flex items-center gap-2">
                                  <Checkbox
                                    id={`${contact.id}-${map.id}`}
                                    checked={nominee?.selectedMaps?.includes(map.id)}
                                    onCheckedChange={(checked) => {
                                      const current = nominee?.selectedMaps || [];
                                      const updated = checked 
                                        ? [...current, map.id]
                                        : current.filter(id => id !== map.id);
                                      updateNomineeShare(contact.id, false, updated);
                                    }}
                                  />
                                  <Label htmlFor={`${contact.id}-${map.id}`} className="text-sm font-normal cursor-pointer">
                                    {map.name}
                                  </Label>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {selectedNominees.length === 0 && contacts.length > 0 && (
              <p className="text-sm text-destructive">Please select at least one nominee</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Navigation buttons */}
      <div className="flex items-center justify-between pt-4">
        <Button
          variant="ghost"
          onClick={() => currentStep > 1 ? setCurrentStep(currentStep - 1) : navigate('/continuity')}
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          {currentStep > 1 ? 'Back' : 'Cancel'}
        </Button>

        {currentStep < 3 ? (
          <Button
            onClick={() => setCurrentStep(currentStep + 1)}
            disabled={!canProceed()}
          >
            Next
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        ) : (
          <Button
            onClick={handleActivate}
            disabled={saving || selectedNominees.length === 0}
            className="min-w-[140px]"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <CheckCircle2 className="w-4 h-4 mr-2" />
            )}
            Activate Continuity
          </Button>
        )}
      </div>

      {/* Review summary on step 3 */}
      {currentStep === 3 && selectedNominees.length > 0 && (
        <Card className="border border-primary/30 bg-primary/5">
          <CardContent className="p-4 space-y-3">
            <h3 className="font-medium">Summary</h3>
            <ul className="text-sm text-muted-foreground space-y-1">
              <li>• Check-in: {FREQUENCY_PRESETS.find(p => p.value === selectedFrequency)?.label}</li>
              <li>• Contact: {[useEmail && 'Email', useSms && 'SMS'].filter(Boolean).join(' & ')}</li>
              <li>• Nominees: {selectedNominees.length} {selectedNominees.length === 1 ? 'person' : 'people'} selected</li>
              <li>• Rechecks before escalation: 3 (default)</li>
            </ul>
            <p className="text-xs text-muted-foreground border-t border-primary/20 pt-2">
              Want to adjust the number of rechecks or add a custom message to nominees?{' '}
              <Link to="/continuity/settings" className="text-primary font-medium hover:underline">
                Go to Continuity Settings →
              </Link>
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default ContinuityWizard;
